import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { capability, DFY_BUNDLES, FESTIVAL_LANE } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const ticketing = capability('ticketing')!
const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!
const festivalPlatform = FESTIVAL_LANE.find((f) => f.id === 'festival-platform')!

const PROBLEMS = [
  {
    problem: 'Ticket fees embarrass the box office',
    description:
      'General-purpose ticketing platforms stack a service fee, a per-ticket charge, and processing on every order. Your patrons see it at checkout, and it reads as your fee.',
  },
  {
    problem: 'Your concert lives on someone else’s page',
    description:
      'Buyers get bounced to a third-party checkout with someone else’s branding, upsells for other events, and no way back to your site.',
  },
  {
    problem: 'The audience data stays with the vendor',
    description:
      'Who bought, who came, who returns every season: on most platforms that history belongs to the ticketing company, not to you.',
  },
  {
    problem: 'Event night is still manual',
    description: 'Will-call lists printed at midnight, no check-in flow, and no report the morning after.',
  },
]

const FAQS = [
  {
    q: 'What does ticketing cost?',
    a: `$${ticketing.priceMonthly} a month plus ${ticketing.perUnitFee}. Selling a ticket costs nothing beyond the 1.5%; the monthly price is box-office management: tiers, capacity, QR check-in, and reporting. Stripe card processing applies as with any payment.`,
  },
  {
    q: 'Who runs it?',
    a: `Today our team sets up and runs ticketing for you; self-serve controls are rolling out. If you want event operations fully off your plate, that lives in Concierge (${concierge.price}); festivals get it inside the ${festivalPlatform.name} lane at ${festivalPlatform.price}.`,
  },
  {
    q: 'Do buyers leave our website?',
    a: 'No. Tickets sell on your site, under your brand, and the buyer lands back in your audience record alongside their email and giving history.',
  },
  {
    q: 'What about free events and RSVPs?',
    a: 'RSVP events carry no per-ticket fee. You only see the 1.5% when money changes hands.',
  },
]

export default function TicketingPage() {
  const breadcrumbs = generateBreadcrumbs('/services/ticketing', breadcrumbLabels)

  useSEO({
    title: 'Ticketing for Music Organizations',
    description:
      'Sell tickets on your own website: $25/mo plus 1.5% per ticket, with QR check-in and reporting. Far below big-platform fee stacks, and your audience data stays yours.',
    url: '/services/ticketing',
    canonical: '/services/ticketing',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Ticketing for Music Organizations',
      description:
        'Concert and event ticketing on your own website with flat monthly pricing, 1.5% per ticket, and QR check-in, with full box-office operations available in Concierge engagements.',
      url: '/services/ticketing',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.12 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Sell tickets on your own site</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your concerts, your checkout, your audience data. Tiers, capacity, QR check-in, and reporting, with 1.5% per
          ticket instead of a platform fee stack.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          ${ticketing.priceMonthly} a month for box-office management. Selling a ticket costs nothing beyond the 1.5%,
          and RSVPs for free events cost nothing at all.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/compare/eventbrite">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Compare vs Eventbrite
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why not just use a ticketing platform?</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Unified CRM differentiator */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">Every buyer joins your audience</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Ticket buyers land in the same audience record as your subscribers and donors. Next season you can email
          everyone who came last year, and your year-end appeal can start with the people who already buy tickets.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          Pairs with{' '}
          <Link to="/services/email-marketing" className="text-primary underline">
            Email Marketing
          </Link>{' '}
          and{' '}
          <Link to="/services/donor-management" className="text-primary underline">
            Donor Management
          </Link>
          .
        </p>
      </section>

      {/* Pricing */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">Two ways to run it</h2>
        <div ref={pricingRef} className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
          <motion.div
            className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <h3 className="mb-2 text-2xl font-bold">{ticketing.name}</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold">${ticketing.priceMonthly}</span>
              <span className="text-muted-foreground">/month + {ticketing.perUnitFee}</span>
            </div>
            <ul className="mb-8 space-y-3">
              {[
                'Events, tiers, capacity',
                'QR check-in on event night',
                'Sales reporting',
                'Free events and RSVPs carry no per-ticket fee',
                'Set up and run for you by our team today',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/get-started" className="block">
              <Button className="w-full cursor-pointer">Get Started</Button>
            </Link>
          </motion.div>
          <motion.div
            className="pricing-card border-border rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary mb-2 text-sm font-medium">Done For You</div>
            <h3 className="mb-2 text-2xl font-bold">Box office, run for you</h3>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-3xl font-bold">{concierge.name}</span>
            </div>
            <p className="text-primary mb-4 text-lg font-bold">{concierge.price}</p>
            <ul className="mb-8 space-y-3">
              {[
                'Event setup and on-sale management',
                'Reporting through the season',
                'Check-in support on event day',
                `Festivals: included in ${festivalPlatform.name} at ${festivalPlatform.price}`,
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/services/done-for-you#concierge" className="block">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More
              </Button>
            </Link>
          </motion.div>
        </div>
        <p className="text-muted-foreground text-center text-sm">
          Product details live on the{' '}
          <Link to="/platform/features/events" className="text-primary underline">
            Events feature page
          </Link>
          . Pay annually on any plan and get 2 months free.
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
